import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AccessPlan, Payment, Installment } from '../../../../core/generated';

export type PaymentMethodType = 'PIX' | 'BOLETO' | 'CREDIT_CARD';

@Injectable({ providedIn: 'root' })
export class ExternalPayerService {
  private http = inject(HttpClient);

  // ── TITULAR ────────────────────────────────────────────────

  addExternalPayer(planId: number, payerCnpj: string): Observable<AccessPlan> {
    return this.http.post<AccessPlan>(`/access-plans/${planId}/add-external-payer`, { payerCnpj });
  }

  requestPayment(planId: number): Observable<AccessPlan> {
    return this.http.post<AccessPlan>(`/access-plans/${planId}/request-payment`, null);
  }

  // ── PAGADOR ────────────────────────────────────────────────

  getPendingPlans(payerCnpj: string): Observable<AccessPlan[]> {
    return this.http.get<AccessPlan[]>(`/access-plans/payer/${payerCnpj}/pending-acceptance`);
  }

  getPendingPlansByUserCpf(userCpf: string): Observable<AccessPlan[]> {
    return this.http.get<AccessPlan[]>(`/access-plans/payer-by-cpf/${userCpf}/pending-acceptance`);
  }

  getPayerPlans(payerCnpj: string): Observable<AccessPlan[]> {
    return this.http.get<AccessPlan[]>(`/access-plans/payer/${payerCnpj}`);
  }

  getAcceptedPlansByUserCpf(userCpf: string): Observable<AccessPlan[]> {
    return this.http.get<AccessPlan[]>(`/access-plans/payer-by-cpf/${userCpf}/accepted`);
  }

  acceptPayment(planId: number): Observable<AccessPlan> {
    return this.http.put<AccessPlan>(`/access-plans/${planId}/accept-payment`, null);
  }

  rejectPayment(planId: number): Observable<AccessPlan> {
    return this.http.put<AccessPlan>(`/access-plans/${planId}/reject-payment`, null);
  }

  confirmPayment(planId: number, method: PaymentMethodType, numberOfInstallments: number): Observable<Payment> {
    const params = new HttpParams()
      .set('method', method)
      .set('numberOfInstallments', numberOfInstallments.toString());
    return this.http.post<Payment>(`/access-plans/${planId}/confirm-payment`, null, { params });
  }

  // ── PARCELAS ───────────────────────────────────────────────

  getPayerPayments(payerCnpj: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`/payments/cpfcnpj/${payerCnpj}`);
  }

  getPayerPaymentsByUserCpf(userCpf: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`/payments/payer-by-cpf/${userCpf}`);
  }

  getInstallments(paymentId: number): Observable<Installment[]> {
    return this.http.get<Installment[]>(`/payments/${paymentId}/installments`);
  }

  payInstallment(installmentId: number): Observable<Payment> {
    return this.http.post<Payment>(`/payments/installments/${installmentId}/pay`, null);
  }
}
